import { useState, useMemo, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search, SlidersHorizontal, X, ChevronRight, Package, MessageCircle,
  FileText, Star, Zap, Award, Tag, Phone, ShoppingCart
} from "lucide-react";
import { useCatalogStore } from "../admin/store/catalogStore";
import type { CatalogProduct, Brand, Category } from "../admin/types/catalog";
import RequestQuoteModal from "../components/RequestQuoteModal";
import BuyNowModal from "../components/BuyNowModal";

// ─── Scroll Animation ────────────────────────────────────────────────────────
function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("visible"); }),
      { threshold: 0.08 }
    );
    const el = ref.current;
    if (el) el.querySelectorAll(".animate-on-scroll").forEach((c) => observer.observe(c));
    return () => observer.disconnect();
  }, []);
  return ref;
}

function formatLKR(v: number) {
  return `Rs. ${v.toLocaleString("en-LK")}`;
}

// ─── Product Card ─────────────────────────────────────────────────────────────
function ProductCard({ product, brand, category, onRequestQuote, onBuyNow }: { product: CatalogProduct; brand?: Brand; category?: Category; onRequestQuote: (item: { id: string; name: string; type: 'product' | 'package'; price: number; image?: string; source?: string }) => void; onBuyNow: (item: { id: string; name: string; type: 'product' | 'package'; price: number; image?: string; source?: string }) => void }) {
  const navigate = useNavigate();
  const whatsappNumber = "94702161323";
  const message = `Hi K-Tec! I'm interested in the ${product.name} (${product.sku}). Price: ${formatLKR(product.regularPrice)}. Please provide more details.`;

  return (
    <div className="group bg-white rounded-3xl border border-gray-200/60 overflow-hidden hover:shadow-xl transition-all duration-300">
      {/* Image */}
      <div
        className="relative aspect-square bg-gray-50 cursor-pointer overflow-hidden"
        onClick={() => navigate(`/products/${product.id}`)}
      >
        {product.mainImage ? (
          <img
            src={product.mainImage}
            alt={product.name}
            className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="w-12 h-12 text-gray-200" />
          </div>
        )}
        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5">
          {product.isBestSeller && <span className="bg-[#7FA86A] text-white text-[10px] font-bold px-2.5 py-1 rounded-full">Best Seller</span>}
          {product.isNewProduct && <span className="bg-emerald-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full">New</span>}
          {product.isFeatured && <span className="bg-violet-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full">Featured</span>}
          {product.salePrice && product.salePrice < product.regularPrice && (
            <span className="bg-red-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full">
              SALE
            </span>
          )}
        </div>
        {!product.inStock && (
          <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
            <span className="bg-gray-800 text-white text-[11px] font-semibold px-4 py-1.5 rounded-full">Out of Stock</span>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-1">
          {brand && <span className="text-[11px] text-[#7FA86A] font-semibold uppercase tracking-wide">{brand.name}</span>}
          {category && <span className="text-[10px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{category.name}</span>}
        </div>
        <h3
          className="text-[14px] font-semibold text-gray-900 mb-0.5 leading-snug cursor-pointer hover:text-[#7FA86A] transition-colors line-clamp-2"
          onClick={() => navigate(`/products/${product.id}`)}
        >
          {product.name}
        </h3>
        {product.modelNumber && <p className="text-[11px] text-gray-400 mb-2">{product.modelNumber}</p>}
        {product.shortDescription && <p className="text-[12px] text-gray-500 mb-3 line-clamp-2">{product.shortDescription}</p>}

        {/* Price */}
        <div className="flex items-baseline gap-2 mb-3">
          {product.salePrice && product.salePrice < product.regularPrice ? (
            <>
              <span className="text-[18px] font-bold text-[#7FA86A]">{formatLKR(product.salePrice)}</span>
              <span className="text-[13px] text-gray-400 line-through">{formatLKR(product.regularPrice)}</span>
            </>
          ) : (
            <span className="text-[18px] font-bold text-gray-900">{formatLKR(product.regularPrice)}</span>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={() => onBuyNow({ id: product.id, name: product.name, type: 'product', price: product.salePrice && product.salePrice < product.regularPrice ? product.salePrice : product.regularPrice, image: product.mainImage, source: 'products_list' })}
            className="flex-1 h-9 rounded-xl bg-[#7FA86A] text-white text-[12px] font-semibold hover:bg-[#658C53] transition-colors flex items-center justify-center gap-1"
          >
            <ShoppingCart className="w-3.5 h-3.5" /> Buy Now
          </button>
          <button
            onClick={() => onRequestQuote({ id: product.id, name: product.name, type: 'product', price: product.salePrice && product.salePrice < product.regularPrice ? product.salePrice : product.regularPrice, image: product.mainImage, source: 'products_list' })}
            className="w-9 h-9 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-600 flex items-center justify-center transition-colors"
            aria-label="Request Quote"
            title="Request Quote"
          >
            <FileText className="w-4 h-4" />
          </button>
          <button
            onClick={() => navigate(`/products/${product.id}`)}
            className="w-9 h-9 rounded-xl bg-gray-900 text-white flex items-center justify-center hover:bg-gray-800 transition-colors"
            aria-label="View Details"
            title="View Details"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function Products() {
  const { brands, categories, catalogProducts } = useCatalogStore();
  const ref = useInView();

  const [search, setSearch] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubcategory, setSelectedSubcategory] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState<"all" | "featured" | "new" | "bestseller">("all");
  const [quoteModal, setQuoteModal] = useState<{ id: string; name: string; type: 'product' | 'package'; price: number; image?: string; source?: string } | null>(null);
  const [buyNowModal, setBuyNowModal] = useState<{ id: string; name: string; type: 'product' | 'package'; price: number; image?: string; source?: string } | null>(null);

  const topCategories = useMemo(() => categories.filter((c) => !c.parentId && c.isActive).sort((a, b) => a.displayOrder - b.displayOrder), [categories]);
  const subCategories = useMemo(() => categories.filter((c) => !!c.parentId && c.isActive).sort((a, b) => a.displayOrder - b.displayOrder), [categories]);

  const activeBrands = useMemo(() => brands.filter((b) => b.isActive).sort((a, b) => a.displayOrder - b.displayOrder), [brands]);

  // Active products only
  const activeProducts = useMemo(() => catalogProducts.filter((p) => p.status === "active"), [catalogProducts]);

  const filtered = useMemo(() => {
    return activeProducts.filter((p) => {
      if (activeTab === "featured" && !p.isFeatured) return false;
      if (activeTab === "new" && !p.isNewProduct) return false;
      if (activeTab === "bestseller" && !p.isBestSeller) return false;
      if (search && !p.name.toLowerCase().includes(search.toLowerCase()) && !p.modelNumber?.toLowerCase().includes(search.toLowerCase())) return false;
      if (selectedBrand && p.brandId !== selectedBrand) return false;
      if (selectedSubcategory && p.subcategoryId !== selectedSubcategory && p.categoryId !== selectedSubcategory) return false;
      else if (!selectedSubcategory && selectedCategory && p.categoryId !== selectedCategory) return false;
      if (priceMin && p.regularPrice < +priceMin) return false;
      if (priceMax && p.regularPrice > +priceMax) return false;
      return true;
    });
  }, [activeProducts, search, selectedBrand, selectedCategory, selectedSubcategory, priceMin, priceMax, activeTab]);

  const getBrand = (id: string) => brands.find((b) => b.id === id);
  const getCategory = (id: string) => categories.find((c) => c.id === id);
  const handleRequestQuote = (item: { id: string; name: string; type: 'product' | 'package'; price: number; image?: string; source?: string }) => setQuoteModal(item);
  const handleBuyNow = (item: { id: string; name: string; type: 'product' | 'package'; price: number; image?: string; source?: string }) => setBuyNowModal(item);

  const hasFilters = search || selectedBrand || selectedCategory || selectedSubcategory || priceMin || priceMax;

  const clearFilters = () => {
    setSearch(""); setSelectedBrand(""); setSelectedCategory(""); setSelectedSubcategory(""); setPriceMin(""); setPriceMax(""); setActiveTab("all");
  };

  return (
    <div ref={ref} className="min-h-screen bg-[#F5F5F7]">
      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section className="bg-[#1D1D1F] text-white py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <div className="animate-on-scroll" style={{ opacity: 0, transform: "translateY(20px)", transition: "all 0.6s ease" }}>
            <span className="inline-block bg-[#7FA86A]/20 text-[#7FA86A] text-[12px] font-semibold px-3 py-1 rounded-full mb-4 uppercase tracking-wider">Product Catalog</span>
            <h1 className="text-[36px] md:text-[48px] font-semibold tracking-tight mb-4">
              Security Technology
              <span className="text-[#7FA86A]"> Solutions</span>
            </h1>
            <p className="text-[16px] text-gray-400 max-w-2xl mx-auto">
              Professional-grade CCTV, smart security, and networking solutions from the world's leading brands.
            </p>
          </div>

          {/* Brand Pills */}
          <div className="flex flex-wrap justify-center gap-2 mt-8">
            <button
              onClick={() => setSelectedBrand("")}
              className={`px-4 py-2 rounded-full text-[13px] font-medium transition-all ${!selectedBrand ? "bg-[#7FA86A] text-white" : "bg-white/10 text-gray-300 hover:bg-white/20"}`}
            >
              All Brands
            </button>
            {activeBrands.map((b) => (
              <button
                key={b.id}
                onClick={() => setSelectedBrand(b.id)}
                className={`px-4 py-2 rounded-full text-[13px] font-medium transition-all ${selectedBrand === b.id ? "bg-[#7FA86A] text-white" : "bg-white/10 text-gray-300 hover:bg-white/20"}`}
              >
                {b.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── Main Content ──────────────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 py-10">
        {/* Search + Filter Bar */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products, models..."
              className="w-full h-12 pl-11 pr-4 rounded-2xl bg-white border border-gray-200/60 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#7FA86A]/20 shadow-sm"
            />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-lg hover:bg-gray-100 flex items-center justify-center">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            )}
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`h-12 px-5 rounded-2xl border text-[14px] font-medium flex items-center gap-2 transition-all shadow-sm ${showFilters || hasFilters ? "bg-[#1D1D1F] text-white border-transparent" : "bg-white text-gray-600 border-gray-200/60 hover:border-gray-300"}`}
          >
            <SlidersHorizontal className="w-4 h-4" />
            Filters {hasFilters && <span className="bg-[#7FA86A] text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">!</span>}
          </button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="bg-white rounded-2xl border border-gray-200/60 p-5 mb-6 shadow-sm">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-[12px] font-semibold text-gray-500 mb-2">Brand</label>
                <select value={selectedBrand} onChange={(e) => setSelectedBrand(e.target.value)} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] bg-white focus:outline-none">
                  <option value="">All Brands</option>
                  {activeBrands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-gray-500 mb-2">Category</label>
                <select value={selectedCategory} onChange={(e) => { setSelectedCategory(e.target.value); setSelectedSubcategory(""); }} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] bg-white focus:outline-none">
                  <option value="">All Categories</option>
                  {topCategories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-gray-500 mb-2">Subcategory</label>
                <select value={selectedSubcategory} onChange={(e) => setSelectedSubcategory(e.target.value)} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] bg-white focus:outline-none">
                  <option value="">All</option>
                  {(selectedCategory ? subCategories.filter(c => c.parentId === selectedCategory) : subCategories).map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-gray-500 mb-2">Price Range (Rs.)</label>
                <div className="flex gap-2">
                  <input type="number" value={priceMin} onChange={(e) => setPriceMin(e.target.value)} placeholder="Min" className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none" />
                  <input type="number" value={priceMax} onChange={(e) => setPriceMax(e.target.value)} placeholder="Max" className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none" />
                </div>
              </div>
            </div>
            {hasFilters && (
              <div className="mt-3 flex justify-end">
                <button onClick={clearFilters} className="h-9 px-4 rounded-xl text-[13px] text-gray-500 hover:bg-gray-100">Clear All Filters</button>
              </div>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
          {[
            { key: "all", label: "All Products", icon: Package },
            { key: "featured", label: "Featured", icon: Star },
            { key: "new", label: "New Arrivals", icon: Zap },
            { key: "bestseller", label: "Best Sellers", icon: Award },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key as any)}
              className={`flex-shrink-0 h-10 px-4 rounded-xl text-[13px] font-medium flex items-center gap-2 transition-colors ${activeTab === key ? "bg-[#1D1D1F] text-white" : "bg-white text-gray-500 border border-gray-200/60 hover:border-gray-300"}`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        {/* Results Count */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-[13px] text-gray-400">
            {filtered.length} product{filtered.length !== 1 ? "s" : ""} found
          </p>
        </div>

        {/* Product Grid */}
        {activeProducts.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-3xl border border-gray-200/60">
            <Package className="w-16 h-16 mx-auto mb-4 text-gray-200" />
            <h3 className="text-[18px] font-semibold text-gray-900 mb-2">No products yet</h3>
            <p className="text-[14px] text-gray-400 max-w-sm mx-auto mb-6">
              Products added through the admin panel will appear here automatically.
            </p>
            <a
              href={`https://wa.me/94702161323?text=${encodeURIComponent("Hi K-Tec! I'd like to inquire about your security products.")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 h-11 px-6 rounded-2xl bg-emerald-500 text-white text-[14px] font-medium hover:bg-emerald-600"
            >
              <MessageCircle className="w-4 h-4" /> Contact via WhatsApp
            </a>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-3xl border border-gray-200/60">
            <Search className="w-16 h-16 mx-auto mb-4 text-gray-200" />
            <h3 className="text-[18px] font-semibold text-gray-900 mb-2">No products match your search</h3>
            <p className="text-[14px] text-gray-400 mb-4">Try adjusting your filters or search terms.</p>
            <button onClick={clearFilters} className="h-10 px-5 rounded-xl bg-gray-900 text-white text-[13px] font-medium">Clear Filters</button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {filtered.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                brand={getBrand(product.brandId)}
                category={getCategory(product.subcategoryId || product.categoryId)}
                onRequestQuote={handleRequestQuote}
                onBuyNow={handleBuyNow}
              />
            ))}
          </div>
        )}

        {/* Quote Modal */}
        {quoteModal && (
          <RequestQuoteModal
            isOpen={true}
            onClose={() => setQuoteModal(null)}
            item={quoteModal}
          />
        )}

        {/* Buy Now Modal */}
        {buyNowModal && (
          <BuyNowModal
            isOpen={true}
            onClose={() => setBuyNowModal(null)}
            item={buyNowModal}
          />
        )}

        {/* CTA Section */}
        <div className="mt-16 bg-[#1D1D1F] rounded-3xl p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="text-[24px] font-semibold text-white mb-2">Need a Custom Solution?</h2>
            <p className="text-gray-400 text-[15px]">Our experts will design the perfect security system for your property.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href={`https://wa.me/94702161323?text=${encodeURIComponent("Hi K-Tec! I need a custom security solution for my property.")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="h-12 px-6 rounded-2xl bg-emerald-500 text-white font-semibold text-[14px] hover:bg-emerald-600 flex items-center gap-2"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp Us
            </a>
            <Link to="/contact" className="h-12 px-6 rounded-2xl bg-white/10 text-white font-semibold text-[14px] hover:bg-white/20 flex items-center gap-2">
              <Phone className="w-4 h-4" /> Get a Quote
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
